import { getLogger } from "@expert/logging";

import { injectExternalScript, injectInternalScript } from "./script-injection";

declare global {
    interface Window {
        Appcues?: {
            page: () => void;
            identify: (user: string, metadata: object) => void;
            track: (eventName: string, eventAttributes: Record<string, unknown>) => void;
        };
    }
}

const logger = getLogger({
    module: "appcues",
});

/**
 * Converts an array to a comma-separated string, or returns undefined if not an array or empty.
 * Logs a warning if the value is truthy but not an array.
 */
function safeArrayToString(value: unknown, paramName: string): string | undefined {
    if (Array.isArray(value)) {
        return value.length > 0 ? value.toString() : undefined;
    } else if (value) {
        logger.warn({ [paramName]: value }, `Appcues: ${paramName} is not an array`);
    }
    return undefined;
}

interface AppcuesOptions {
    isEnabled: boolean;
    url: string;
    appName: string;
    channel: string;
}

export const appcuesBaseAttributes: Record<string, unknown> = {};

export const initAppcues = ({ isEnabled, url, appName, channel }: AppcuesOptions) => {
    if (isEnabled && url && appName) {
        appcuesBaseAttributes.appName = appName;
        appcuesBaseAttributes.channel = channel;
        injectInternalScript("AppcuesSettingsScript", "window.AppcuesSettings = { enableURLDetection: true };");
        injectExternalScript("AppcuesScript", url);
        logger.debug(`Appcues script injected.`);
    } else {
        logger.warn(`Did NOT inject appcues script`);
    }
};

interface AppcuesIdentifier {
    identity: string;
    name: string;
    email?: string;
    /** current or default partner for a user */
    partner?: string;
    /** active directory groups associated with the user */
    activeDirectoryGroups?: string[];
}

export const setAppcuesIdentifier = ({
    identity,
    name,
    email = identity,
    partner,
    activeDirectoryGroups,
}: AppcuesIdentifier) => {
    if (!identity) return;
    try {
        // ? appcues doesn't support arrays, converting arrays to strings
        const adGroupsString = safeArrayToString(activeDirectoryGroups, "activeDirectoryGroups");

        Object.assign(appcuesBaseAttributes, {
            ...(partner && { partner }),
            ...(adGroupsString && { activeDirectoryGroups: adGroupsString }),
        });

        window.Appcues?.identify(identity, {
            name,
            email,
            partner,
            activeDirectoryGroups: adGroupsString,
        });

        logger.info("Appcues user context updated");
    } catch (error) {
        logger.warn({ error }, "Appcues failed to update user context");
    }
};

export const trackAppcuesEvent = (eventName: string, eventAttributes: Record<string, unknown>) => {
    window.Appcues?.track(eventName, { ...appcuesBaseAttributes, ...eventAttributes });
};
